import { _js } from '@ifixit/localize';
import { SectionHeading } from '../section_heading';
import { Button } from '@ifixit/ui/components/button';
import { OrderType } from './orders_types';
import { Badge } from '@ifixit/ui/components/badge';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Table } from '@ifixit/ui/components/table';
import type { TableCell } from '@ifixit/ui/components/table';
import { useMediaQuery } from '@ifixit/ui/hooks/use-media-query';
import { memo, useMemo } from 'react';
import { useTranslations } from '@ifixit/i18n/hooks';

export const OrdersTable = memo(
   ({
      orders,
   }: {
      orders: OrderType[];
   }) => {
      const isMobile = useMediaQuery('(max-width: 768px)');

      return (
         <div className="flex flex-col w-full gap-6">
            <SectionHeading>{_js('Order History')}</SectionHeading>
            {isMobile ? <MobileOrderList orders={orders} /> : <DesktopOrderTable orders={orders} />}
         </div>
      );
   }
);

OrdersTable.displayName = 'OrdersTable';

// Move statuses outside function to avoid recreation on every call
const ORDER_STATUSES = [
   { text: _js('Order Placed') },
   { text: _js('Processing Order') },
   { text: _js('Preparing For Shipment') },
   { text: _js('Shipped'), isGreen: true },
   { text: _js('Complete'), isGreen: true },
];

// Helper function to convert thumbnail URL to standard URL
function getStandardImageUrl(thumbnailUrl: string | undefined): string {
   if (!thumbnailUrl) return '';
   return thumbnailUrl.replace('.thumbnail', '.standard');
}

// Cache status styles to avoid repeated computation
type StatusStyles = {
   badge: string;
   icon: string;
   text: string;
};

const statusStylesCache = new Map<string, StatusStyles>();

const getStatusStyles = (statusText: string): StatusStyles => {
   const trimmedStatus = statusText.trim();

   if (statusStylesCache.has(trimmedStatus)) {
      return statusStylesCache.get(trimmedStatus)!;
   }

   const checkStatus = ORDER_STATUSES.find(s => s.text === trimmedStatus);
   const styles = {
      badge: checkStatus?.isGreen ? 'bg-lime-200' : 'bg-brand-200',
      icon: checkStatus?.isGreen ? 'text-lime-600' : 'text-brand-600',
      text: checkStatus?.isGreen ? 'text-lime-900' : 'text-brand-900',
   };

   statusStylesCache.set(trimmedStatus, styles);
   return styles;
};

// Order table column definitions - extracted as constant for performance
const ORDER_TABLE_CELLS = [
   { id: 'thumbnail', label: '', type: 'image' },
   { id: 'order', label: _js('Order'), type: 'text' },
   { id: 'status', label: _js('Status'), type: 'text', cellWidth: 'min-w-[40%]' },
   { id: 'action', label: '', type: 'interactive' },
] as TableCell[];

// Memoized cell components to prevent recreation on every render
const ThumbnailCell = memo(({ order }: { order: OrderType }) => (
   <a href={order.invoice_link}>
      <div className="size-[57px] border border-gray-200 rounded-lg overflow-hidden">
         <img
            src={getStandardImageUrl(order.thumbnail)}
            alt={order.order_name}
            className="size-full object-cover"
         />
      </div>
   </a>
));

const OrderCell = memo(({ order }: { order: OrderType }) => (
   <a
      href={order.invoice_link}
      className={`flex flex-col gap-1 text-gray-900 [&_span]:text-gray-600`}
   >
      <div className="font-semibold">{order.shopifyName ?? order.orderid}</div>
      <div>{`${order.item_count} ${order.item_count === 1 ? _js('Item') : _js('Items')}`}</div>
      <div>{getFormattedTotal(order.total, order.currency_type)}</div>
   </a>
));

const StatusCell = memo(({ order }: { order: OrderType }) => {
   const statusStyles = getStatusStyles(order.status);
   return (
      <a href={order.invoice_link} className={`flex flex-col gap-1 text-gray-900`}>
         <Badge className={statusStyles.badge}>
            <FaIcon name="badge-check" className={statusStyles.icon} />
            <span className={statusStyles.text}>{order.status}</span>
         </Badge>
         <div>{formatOrderDate(order.init_date)}</div>
      </a>
   );
});

function ActionCell({ order }: { order: OrderType }) {
   const t = useTranslations('UserPage');
   return (
      <div className="flex flex-col gap-1">
         {order.isReviewable && order.reviewLink && (
            <Button asChild className="button-outline button-xs">
               <a href={order.reviewLink}>{t('review')}</a>
            </Button>
         )}
      </div>
   );
}

// Memoized mobile order card component
const MobileOrderCard = memo(({ order }: { order: OrderType }) => {
   const statusStyles = getStatusStyles(order.status);
   const itemCount = order.item_count || 1;
   const itemText = ___p(itemCount, 'Item', 'Items');

   return (
      <a
         href={order.invoice_link}
         className="flex flex-col p-4 gap-3 rounded-2xl border-1 text-gray-900 border-gray-300 bg-white no-underline"
      >
         <div className="flex flex-col gap-1">
            <Badge className={statusStyles.badge}>
               <FaIcon name="badge-check" className={statusStyles.icon} />
               <span className={statusStyles.text}>{order.status}</span>
            </Badge>
            <div className="text-gray-600">{formatOrderDate(order.init_date)}</div>
         </div>
         <div className="size-full rounded-lg border border-gray-200 overflow-hidden">
            <img
               src={getStandardImageUrl(order.thumbnail)}
               alt={order.order_name}
               className="size-full object-cover object-center"
            />
         </div>
         <div className="flex flex-col gap-1">
            <div className="font-semibold">{`#${order.orderid}`}</div>
            <div className="text-gray-600">{`${itemCount} ${itemText}`}</div>
         </div>
         <div className="font-semibold ml-auto">
            {getFormattedTotal(order.total, order.currency_type)}
         </div>
      </a>
   );
});

const MobileOrderList = memo(({ orders }: { orders: OrderType[] }) => (
   <div className="grid grid-cols-2 [@media(min-width:520px)]:grid-cols-3 gap-3">
      {orders.map((order: OrderType) => (
         <MobileOrderCard key={order.orderid} order={order} />
      ))}
   </div>
));

const DesktopOrderTable = memo(({ orders }: { orders: OrderType[] }) => {
   const tableData = useMemo(
      () =>
         orders.map((order: OrderType) => ({
            id: order.orderid,
            thumbnail: <ThumbnailCell order={order} />,
            order: <OrderCell order={order} />,
            status: <StatusCell order={order} />,
            action: <ActionCell order={order} />,
         })),
      [orders]
   );

   return <Table data={tableData} cells={ORDER_TABLE_CELLS} localize={false} />;
});

// Add display names for better debugging
ThumbnailCell.displayName = 'ThumbnailCell';
OrderCell.displayName = 'OrderCell';
StatusCell.displayName = 'StatusCell';
ActionCell.displayName = 'ActionCell';
DesktopOrderTable.displayName = 'DesktopOrderTable';
MobileOrderCard.displayName = 'MobileOrderCard';
MobileOrderList.displayName = 'MobileOrderList';

// Cache formatters by currency to avoid recreation
const formattersCache = new Map<string, Intl.NumberFormat>();

function getFormattedTotal(total: number, currency_type: string) {
   if (!formattersCache.has(currency_type)) {
      formattersCache.set(
         currency_type,
         new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency_type,
         })
      );
   }
   return formattersCache.get(currency_type)!.format(total);
}

// Cache date formatters to avoid repeated Date object creation
const dateFormatter = new Intl.DateTimeFormat(undefined, {
   year: 'numeric',
   month: 'short',
   day: 'numeric',
});

function formatOrderDate(timestamp: number): string {
   return dateFormatter.format(new Date(timestamp * 1000));
}
